/**
 * TelusConsent Component
 *
 * Electronic transmission authorization and consent form
 * for claims submission and benefit assignment
 */

import React from "react";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

function TelusConsent({
  checked,
  onChange = () => {},
  answers = {},
  signatureRef,
  onClearSignature,
  readOnly,
  version,
}) {
  if (!checked) {
    return null;
  }

  return (
    <div>
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Electronic transmission authorization and consent form
      </Typography>
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Instructions: This form must be filled out when claims are submitted
        electronically by the provider on the patient&apos;s behalf. Please
        retain this form in the patient&apos;s file for verification purposes
        for two years following closure of the patient file. <br />
        <br />
        <b>Consent to collect and exchange personal information</b> <br />
        <i>Purpose</i> <br /> Personal information that we collect and disclose
        about you, and if applicable, is used by the insurer, and/or plan
        administrator of your group benefits plan, its affiliates and their
        service provider(s) for the purposes of assessing eligibility for your
        claims, underwriting, investigating, auditing and otherwise
        administering the group benefits plan, including the investigation of
        fraud and / or plan abuse and for internal data management and data
        analytical purposes.
        <br />
        <br />
        <i>Authorization and consent</i>
        <br />
        I authorize my healthcare provider to collect, use and disclose personal
        information concerning any claims submitted on my behalf with the
        insurer and/or plan administrator and their service provider(s) for the
        above purposes. I authorize such insurer and / or plan administrator and
        their service provider(s) to:
        <br />
        <br />• use my personal information for the above purposes.
        <br />• exchange personal information with any individual or
        organization, including healthcare professionals, investigative
        agencies, insurers and reinsurers, and administrators of government
        benefits, or other benefits programs, other organizations, or service
        providers working with such insurer and/or plan administrator or any of
        the foregoing, when relevant for the above purposes.
        <br />• where applicable exchange personal information concerning any
        claims with any assignee of benefits payable and exchange personal
        information for the above purposes electronically or in any other
        manner.
        <br />
        <br />
        I understand that personal information may be subject to disclosure to
        those authorized under applicable law.
        <br />
        <br />I agree that a photocopy or electronic version of this
        authorization shall be as valid as the original, and may remain in
        effect for the continued administration of the group benefits plan.
        <br />
        <br />
        In the event there is suspicion and/or evidence of fraud and/or plan
        abuse concerning any claims submitted, I acknowledge and agree that the
        insurer and/or plan administrator and their service provider(s) may use
        and disclose relevant personal information to any relevant organization
        including law enforcement bodies, regulatory bodies, government
        organizations, medical suppliers and other insurers, and where
        applicable my employer or benefit plan sponsor, for the purposes of
        investigation and prevention of fraud and/or benefit plan abuse. I
        understand that the submission of fraudulent claims is a criminal
        offence.
        <br />
        <br />
        If there is an overpayment, I authorize the recovery of the full amount
        of the overpayment from any amount payable under the group benefits
        plan, and the exchange of personal information with other persons or
        organizations, including credit agencies and, where applicable, my
        benefit plan sponsor, for that purpose.
        <br />
        <br />
        If the patient is a person other than myself, I confirm that the patient
        has given their consent to provide their personal information for the
        healthcare provider and the insurer and/or plan administrator and their
        service provider(s) to use and disclose their personal information as
        set out above.
        <br />
        <br />
        <b>Benefit assignment form</b>
        <br />
        I hereby assign benefits payable for the eligible claims to the
        healthcare provider responsible for submitting my claims electronically
        to the group benefits plan and I authorize the insurer/plan
        administrator to issue payment directly to such provider. In the event
        my claim(s) are declined by the insurer/plan administrator, I understand
        that I remain responsible for payment to the healthcare provider for any
        ser- vices rendered and/ or supplies provided.
        <br />
        <br />I acknowledge and agree that the insurer/plan administrator is
        under no obligation to accept this benefit assignment form, that any
        benefit payment made in accordance with this benefit assignment form
        will discharge the insurer/plan administrator of its obligations with
        respect to that benefit payment, and that in the event the benefit
        payment is made to me, the insurer/plan administrator will also be
        discharged of its obligation with respect to that benefit payment.
        <br />
        <br />I understand that this assignment will apply to all eligible
        claims submitted electronically by my healthcare provider and that I may
        revoke it at any time by providing written notice to the insurer/plan
        administrator.
        <br />
        <br />
        If I am a spouse or dependent, I confirm that I am authorized by the
        plan member to execute an assignment of benefit payments to the
        healthcare provider.
      </Typography>
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentAcceptTerms || false}
            onChange={(e) => onChange("consentAcceptTerms", e.target.checked)}
            color="primary"
            name="consentAcceptTerms"
            disabled={readOnly}
          />
        }
        label="I accept the terms and conditions"
      />
      <TextField
        onChange={(e) => onChange("firstName", e.target.value)}
        id="outlined-multiline-static"
        label="First Name"
        style={{ marginBottom: 20, marginTop: 10, marginRight: 20 }}
        variant="outlined"
        name="firstName"
        value={answers.firstName || ""}
        disabled={readOnly}
      />
      <TextField
        onChange={(e) => onChange("lastName", e.target.value)}
        id="outlined-multiline-static"
        label="Last Name"
        style={{ marginBottom: 20, marginTop: 10 }}
        variant="outlined"
        name="lastName"
        value={answers.lastName || ""}
        disabled={readOnly}
      />
      <br />
      <TextField
        id="date"
        label="Birth Date"
        type="date"
        defaultValue="2000-00-00"
        variant={"outlined"}
        InputLabelProps={{
          shrink: true,
        }}
        style={{ marginRight: 20 }}
        onChange={(e) => onChange("dob", e.target.value)}
        name="dob"
        value={answers.dob || ""}
        disabled={readOnly}
      />
      <FormControl variant="outlined" style={{ width: 195 }}>
        <InputLabel id="demo-simple-select-outlined-label">Gender</InputLabel>
        <Select
          labelId="demo-simple-select-outlined-label"
          id="demo-simple-select-outlined"
          // value={}
          onChange={(e) => onChange("gender", e.target.value)}
          label="Gender"
          name="gender"
          value={answers.gender || ""}
          disabled={readOnly}
        >
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl>
      <br />
      <br />
      <Typography variant="subtitle2" style={{ textAlign: "left" }}>
        Primary coverage insurer/payer:
      </Typography>
      <TextField
        onChange={(e) => onChange("primaryCoverage", e.target.value)}
        id="outlined-multiline-static"
        label="Answer"
        style={{ marginBottom: 20, marginTop: 10, width: "100%" }}
        variant="outlined"
        name="primaryCoverage"
        value={answers.primaryCoverage || ""}
        disabled={readOnly}
      />
      <Typography variant="subtitle2" style={{ textAlign: "left" }}>
        Primary coverage plan member name:
      </Typography>
      <TextField
        onChange={(e) => onChange("planMember", e.target.value)}
        id="outlined-multiline-static"
        label="Answer"
        style={{ marginBottom: 20, marginTop: 10, width: "100%" }}
        variant="outlined"
        name="planMember"
        value={answers.planMember || ""}
        disabled={readOnly}
      />
      <Typography variant="subtitle2" style={{ textAlign: "left" }}>
        Primary coverage policy number (also referred to as group or contract
        number):
      </Typography>
      <TextField
        onChange={(e) => onChange("policyNumber", e.target.value)}
        id="outlined-multiline-static"
        label="Answer"
        style={{ marginBottom: 20, marginTop: 10, width: "100%" }}
        variant="outlined"
        name="policyNumber"
        value={answers.policyNumber || ""}
        disabled={readOnly}
      />
      <Typography variant="subtitle2" style={{ textAlign: "left" }}>
        Primary coverage certificate (also referred to as member/identification
        number):
      </Typography>
      <TextField
        onChange={(e) => onChange("coverageCert", e.target.value)}
        id="outlined-multiline-static"
        label="Answer"
        style={{ marginBottom: 20, marginTop: 10, width: "100%" }}
        variant="outlined"
        name="coverageCert"
        value={answers.coverageCert || ""}
        disabled={readOnly}
      />
      <Typography variant="subtitle2" style={{ textAlign: "left" }}>
        (Canada Life only) secondary coverage plan member name
      </Typography>
      <TextField
        onChange={(e) => onChange("canadaLife", e.target.value)}
        id="outlined-multiline-static"
        label="Answer"
        style={{ marginBottom: 20, marginTop: 10, width: "100%" }}
        variant="outlined"
        name="canadaLife"
        value={answers.canadaLife || ""}
        disabled={readOnly}
      />
      <br /> <br />
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentTelusSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        All information contained herein is protected by privacy laws including
        the Personal Information Protection and Electronic Documents Act
        (PIPEDA) and all the corresponding provincial legislation. All users
        agree to protect the personal health information contained herein from
        unauthorized use, disclosure, loss, theft, or compromise in accordance
        with the above noted laws and with at least the same care employed to
        protect their own confidential information. Any unauthorized access,
        disclosure or use of this information is illegal.
      </Typography>
      <br />
      <br />
    </div>
  );
}

export default TelusConsent;
